import { PageHero } from '@components/common';
import {
  Search,
  UserCheck,
  Users,
  Rocket,
  Network,
  Check,
  X,
  ArrowRight,
  ChevronDown,
} from 'lucide-react';
import { CALENDLY_URL } from '@constants';
import { useState } from 'react';

const comparisonFeatures = [
  { feature: 'Flexible, monthly contract', cisoNetwork: true, freelance: true, inHouse: false },
  { feature: 'Cost-effective vs full-time', cisoNetwork: true, freelance: true, inHouse: false },
  { feature: 'No recruitment fees', cisoNetwork: true, freelance: true, inHouse: false },
  { feature: 'Large network to choose from', cisoNetwork: true, freelance: false, inHouse: false },
  {
    feature: 'Access to collective network knowledge',
    cisoNetwork: true,
    freelance: false,
    inHouse: false,
  },
  {
    feature: 'Immediate replacement if not a fit',
    cisoNetwork: true,
    freelance: false,
    inHouse: false,
  },
  { feature: '24/7 network support coverage', cisoNetwork: true, freelance: false, inHouse: false },
  {
    feature: 'Sector specialists available',
    cisoNetwork: true,
    freelance: 'takes time',
    inHouse: 'takes time',
  },
];

const steps = [
  {
    icon: Search,
    step: '01',
    title: 'Discovery Call',
    description:
      'It starts with a conversation. We listen carefully to understand your business, security challenges, and ambitions. No pressure, no jargon - just a clear discussion to explore where expert security leadership could make a difference.',
  },
  {
    icon: UserCheck,
    step: '02',
    title: 'Understand Your Goals',
    description:
      "We dig deeper to understand what success looks like for you. Whether it's preparing for an IPO, achieving compliance certification, responding to a breach, or building a security culture, we align around your priorities so every step supports your business objectives.",
  },
  {
    icon: Users,
    step: '03',
    title: 'CISO Matching & Onboarding',
    description:
      'We introduce you to the right CISO from our network - someone whose industry experience and expertise align with your needs. Your CISO embeds in your business, meeting key stakeholders and conducting an initial security assessment.',
  },
  {
    icon: Rocket,
    step: '04',
    title: 'Ongoing Leadership & Network Access',
    description:
      'Your CISO provides continuous strategic security guidance, scaled to your pace and budget. Plus, you gain access to our entire network for specialised expertise, peer consultation, and 24/7 coverage when needed.',
  },
];

const networkBenefits = [
  {
    title: 'Collective Expertise',
    description:
      'Your CISO can tap into the collective knowledge of the entire network. Stuck on a complex architecture decision? Your CISO has peers to consult.',
  },
  {
    title: 'Specialist Access',
    description:
      'Need deep expertise in OT security, cloud architecture, or incident response? The network provides specialists for specific challenges.',
  },
  {
    title: 'Continuity Coverage',
    description:
      'Your CISO goes on holiday or takes sick leave? The network ensures continuity - another experienced CISO can step in if needed.',
  },
  {
    title: 'Peer Validation',
    description:
      "Critical security decision? Your CISO can validate thinking with experienced peers who've faced similar challenges.",
  },
];

const faqs = [
  {
    question: 'What is a fractional CISO?',
    answer:
      'A fractional CISO is an experienced Chief Information Security Officer who works with your business on a part-time, flexible basis. They deliver the same strategic security leadership as a full-time CISO, at a fraction of the cost, helping you build robust security programmes without the overhead of a permanent hire.',
  },
  {
    question: 'How is a fractional CISO different from a part-time or full-time CISO?',
    answer:
      'Fractional CISOs offer the strategic skills and experience of a full-time CISO but work only as much as your business needs - typically 1-3 days per week. This makes them a cost-effective choice for SMEs and growing companies who want high-level security leadership without the £150K-£300K+ salary and benefits package of a permanent executive.',
  },
  {
    question: 'How do you match a CISO to my business?',
    answer:
      'We take time to understand your goals, challenges, industry, and culture, then connect you with a CISO who has relevant experience and sector insight. Our network includes CISOs with backgrounds in financial services, healthcare, technology, manufacturing, retail, and more.',
  },
  {
    question: 'Can your CISOs work with my existing team?',
    answer:
      'Absolutely. We work with, not over, your team - supporting, mentoring, and strengthening internal capability as part of the process. Your CISO becomes part of your leadership team while respecting your existing structure.',
  },
  {
    question: 'How quickly can you get a CISO in place?',
    answer:
      'We can typically match a CISO within days, not weeks. Unlike recruitment processes that take 3-6 months, our network of pre-vetted, experienced CISOs means we can move quickly to address urgent security needs.',
  },
  {
    question: "What if my CISO isn't the right fit?",
    answer:
      "We offer immediate replacement at no additional cost if the match isn't right. With our large network, we can quickly find an alternative CISO whose style and experience better align with your needs.",
  },
  {
    question: "What does 'network access' actually mean?",
    answer:
      "Your CISO can consult with other network members on complex challenges, bring in specialists for specific projects, and ensure continuity during absences. You're not just hiring one person - you're accessing the collective expertise of the entire network.",
  },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-border">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-center justify-between text-left hover:text-teal transition-colors"
      >
        <span className="font-heading font-semibold text-foreground pr-8">{question}</span>
        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}
      >
        <p className="text-muted-foreground leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

export default function HowWeWorkPage() {
  // FAQ structured data for SEO
  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
      <PageHero
        title="How We Find the CISO Who Helps You Get What You Really Want"
        eyebrow="How We Work"
        seoTitle="How We Work | The CISO Network"
        seoDescription="Discover how The CISO Network works. We match you with experienced fractional CISOs. No recruitment fees, no tie-ins, just world-class security leadership."
      />

      {/* Value Props */}
      <section className="py-16 bg-background">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="text-4xl font-heading font-bold text-teal mb-2">Top Tier</div>
              <p className="text-muted-foreground">
                We bring only experienced, proven experts into our network - leaders with deep
                experience and track records of delivering results.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl font-heading font-bold text-teal mb-2">No Fees</div>
              <p className="text-muted-foreground">
                No up-front recruitment fees. Get strategic security support without the hiring
                commitment or cost.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl font-heading font-bold text-teal mb-2">No Tie-ins</div>
              <p className="text-muted-foreground">
                Flexible support with no long-term lock-in required. Scale up, down, or pause as
                your needs change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Choosing a CISO: How We Measure Up
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              The CISO Network provides unmatched scale, expertise, and flexibility. See how we
              compare to other options.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-foreground">
                  <th className="text-left py-4 px-4 font-heading font-semibold text-foreground">
                    Feature
                  </th>
                  <th className="text-center py-4 px-4 font-heading font-semibold text-teal bg-teal/5">
                    The CISO Network
                  </th>
                  <th className="text-center py-4 px-4 font-heading font-semibold text-muted-foreground">
                    Freelance CISO
                  </th>
                  <th className="text-center py-4 px-4 font-heading font-semibold text-muted-foreground">
                    In-house CISO
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((row, i) => (
                  <tr key={i} className="border-b border-border">
                    <td className="py-4 px-4 text-foreground font-medium">{row.feature}</td>
                    <td className="py-4 px-4 text-center bg-teal/5">
                      {row.cisoNetwork === true ? (
                        <Check className="h-5 w-5 text-teal mx-auto" />
                      ) : (
                        <span className="text-muted-foreground text-sm">{row.cisoNetwork}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {row.freelance === true ? (
                        <Check className="h-5 w-5 text-teal mx-auto" />
                      ) : row.freelance === false ? (
                        <X className="h-5 w-5 text-muted-foreground/30 mx-auto" />
                      ) : (
                        <span className="text-muted-foreground text-sm">{row.freelance}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {row.inHouse === true ? (
                        <Check className="h-5 w-5 text-teal mx-auto" />
                      ) : row.inHouse === false ? (
                        <X className="h-5 w-5 text-muted-foreground/30 mx-auto" />
                      ) : (
                        <span className="text-muted-foreground text-sm">{row.inHouse}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* The Network Advantage */}
      <section className="py-24 gradient-navy">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-teal tracking-wide uppercase">
              The Network Advantage
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mt-3 mb-4">
              You're Not Just Getting a CISO - You're Getting a Network
            </h2>
            <p className="text-primary-foreground/60 max-w-3xl mx-auto text-lg">
              Every engagement includes access to our collective expertise. Your CISO can tap into
              the network whenever you need additional support.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {networkBenefits.map((benefit, i) => (
              <div
                key={i}
                className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-teal/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-teal/20 flex items-center justify-center flex-shrink-0">
                    <Network className="h-5 w-5 text-teal" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-primary-foreground mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-primary-foreground/50 text-sm leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Finding Your CISO - Here's How We Work
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our refined approach ensures seamless integration, trusted relationships, and outcomes
              that give you confidence from day one.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="relative text-center group">
                {/* Connector line */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 left-[60%] w-[80%] h-px bg-gradient-to-r from-teal/30 to-transparent" />
                )}

                <div className="text-6xl font-heading font-bold text-teal/10 mb-4 group-hover:text-teal/20 transition-colors duration-500">
                  {step.step}
                </div>
                <div className="h-14 w-14 mx-auto rounded-full border-2 border-teal/30 flex items-center justify-center mb-5 group-hover:border-teal group-hover:bg-teal/10 transition-all duration-300 group-hover:scale-110">
                  <step.icon className="h-6 w-6 text-teal transition-transform duration-300 group-hover:scale-110" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2 group-hover:text-teal transition-colors duration-300">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed group-hover:text-foreground/70 transition-colors duration-300">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-white">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="bg-background rounded-2xl p-8 border border-border">
            {faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Free Up Your Time, Secure Your Business, and Feel in Control
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            At The CISO Network, we bring world-class security leadership to ambitious businesses,
            providing a flexible, affordable, and impactful service from day one. Our proven model,
            exceptional CISOs, and network-first approach give you clarity, confidence, and control.
          </p>
          <p className="text-primary-foreground/60 max-w-2xl mx-auto mb-10">
            Whether you're scaling, preparing for compliance, responding to an incident, or planning
            your next move, we're here to help you achieve the security outcomes that really matter.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
